/**
 * Device secrets (PLAN §9.4, D11, D13): the credentials a device needs — an
 * OBS websocket password, an overlay API's bearer token — kept per tenant and
 * per device in Secrets Manager, never in rows or the studio config.
 *
 * A secret is a small map of the device's option names to strings
 * (`{ password: '…' }`, `{ bearerToken: '…' }`). A node running the studio's
 * production receives it over the site link (`siteLinkSecrets.ts`, sealed to
 * the node's key after a tenant check) and lays it over the device's
 * `options` when it starts the production — so the config says where the
 * device is, and the secret only what must not be written down.
 *
 * The API never answers a value: setting one returns which option names it
 * holds, and nothing reads it back (the CLI does not echo it either).
 */
import { z } from 'zod';
import { isoTimestampWireSchema, studioIdWireSchema } from './primitives.js';

/**
 * A device id as a path segment. A studio config's device keys may also hold
 * `.` and `:` (`configIdSchema`), which no route segment carries; a device
 * whose id has them cannot hold a secret — rename it.
 */
export const deviceIdPathWireSchema = z.string().regex(/^[A-Za-z0-9_][A-Za-z0-9_-]{0,127}$/, 'a device id of letters, digits, `_` and `-`');

/** A device option name a secret may set: an identifier (`password`, `bearerToken`, `oauthClientSecret`). */
export const secretKeyWireSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/, 'an option name: a letter, then letters, digits or _');

/** At most this many options in one secret, and this long a value. */
const MAX_SECRET_KEYS = 16;
const MAX_SECRET_VALUE_CHARS = 4096;

const deviceSecretValuesWireSchema = z
  .record(secretKeyWireSchema, z.string().min(1).max(MAX_SECRET_VALUE_CHARS))
  .refine((values) => Object.keys(values).length >= 1 && Object.keys(values).length <= MAX_SECRET_KEYS, {
    message: `a secret sets 1 to ${String(MAX_SECRET_KEYS)} options`,
  });
export type DeviceSecretValuesWire = z.infer<typeof deviceSecretValuesWireSchema>;

/** What the API says about a secret: never its values. */
export const deviceSecretMetaWireSchema = z
  .object({
    studioId: studioIdWireSchema,
    deviceId: deviceIdPathWireSchema,
    /** The option names it sets, sorted. */
    keys: z.array(secretKeyWireSchema),
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().min(1).max(128),
  })
  .strict();
export type DeviceSecretMetaWire = z.infer<typeof deviceSecretMetaWireSchema>;

export const deviceSecretPathWireSchema = z
  .object({ studioId: studioIdWireSchema.describe('The studio’s id.'), deviceId: deviceIdPathWireSchema.describe('The device’s id in the studio config.') })
  .strict();

export const setDeviceSecretRequestWireSchema = z.object({ values: deviceSecretValuesWireSchema }).strict();

export const deviceSecretResponseWireSchema = z.object({ secret: deviceSecretMetaWireSchema }).strict();
export const listDeviceSecretsResponseWireSchema = z.object({ secrets: z.array(deviceSecretMetaWireSchema) }).strict();
